import { Modal, Select, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import debounce from 'lodash/debounce';

import { simpleFetch } from '../utils/http';
import { ATTRIBUTE_PROPTYPE } from '../constants/commonPropTypes';

const fetchUserByUID = async uid => {
  try {
    return await simpleFetch(`/api/v1/people/${uid}?fields=name,cell_phone,gpmail`);
  } catch (e) {
    message.error(`UIDEditModal - While fetching user: ${uid}`);
    console.error(`UIDEditModal - While fetching user: ${uid}`);
    console.error(e.toString());

    // return empty user object so application doesn't crash
    return {
      person_id: uid,
      genres: [{
        sections: [],
      }],
    };
  }
};

const getPeopleOptionsFromApiResponse = people => people.map(person => {
  const personalSection = person.genres[0].sections.find(section => section.name === 'personal');
  const emailSection = person.genres[0].sections.find(section => section.name === 'email');
  const phoneSection = person.genres[0].sections.find(section => section.name === 'phone');
  const infoArray = [
    personalSection && personalSection.attribute_types[0].attributes[0].value,
    emailSection && emailSection.attribute_types[0].attributes[0].value,
    phoneSection && phoneSection.attribute_types[0].attributes[0].value,
  ];

  return {
    value: person.person_id,
    text: infoArray.filter(info => !!info).join(' - '),
  };
});

const UIDEditModal = ({ attrTypeName, singleton, attributes, updateValue, onClose }) => {
  const [selectedPeople, setSelectedPeople] = useState(null);
  const [peopleOptions, setPeopleOptions] = useState([]);
  const modalVisible = !!(attrTypeName && attributes);

  useEffect(() => {
    if (attributes) {
      // Select.Option requires attributes to be strings
      const currentlySelected = attributes.filter(attribute => !!attribute.value)
        .map(attribute => attribute.value.toString());
      if (singleton) {
        setSelectedPeople(currentlySelected[0]);
      } else {
        setSelectedPeople(currentlySelected);
      }

      // only do API request on mounted component
      if (modalVisible) {
        Promise.all(currentlySelected.map(fetchUserByUID)).then(people => {
          setPeopleOptions(getPeopleOptionsFromApiResponse(people));
        });
      }
    }
  }, [singleton, attributes, modalVisible]);

  const handleSearch = async searchTerm => {
    if (searchTerm.length > 0) {
      try {
        const people = await simpleFetch(`/api/v1/people/search/${searchTerm}?fields=name,cell_phone,gpmail`);
        setPeopleOptions(getPeopleOptionsFromApiResponse(people));
      } catch {
        setPeopleOptions([]);
      }
    }
  };

  const handleOk = () => {
    if (singleton) {
      const userId = parseInt(selectedPeople, 10);
      const validUserId = Number.isNaN(userId) ? undefined : userId;
      updateValue(attrTypeName, validUserId);
    } else {
      updateValue(attrTypeName, selectedPeople.map(person => parseInt(person, 10)));
    }
    setSelectedPeople(null);
    setPeopleOptions([]);
    onClose();
  };

  const handleCancel = () => {
    setSelectedPeople(null);
    setPeopleOptions([]);
    onClose();
  };

  return (
    <Modal
      onCancel={handleCancel}
      onOk={handleOk}
      open={modalVisible}
      title={`Edit ${attrTypeName || ''}`}
    >
      <Select
        allowClear
        showSearch
        defaultActiveFirstOption={false}
        filterOption={false}
        mode={singleton ? 'default' : 'multiple'}
        onChange={people => setSelectedPeople(people)}
        onSearch={debounce(handleSearch, 500)}
        placeholder="Begin typing to search for user..."
        showArrow={false}
        style={{ width: '100%' }}
        value={selectedPeople}
      >
        {peopleOptions.map(person => <Select.Option key={person.value}>{person.text}</Select.Option>)}
      </Select>
    </Modal>
  );
};

UIDEditModal.propTypes = {
  attrTypeName: PropTypes.string,
  singleton: PropTypes.bool,
  attributes: PropTypes.arrayOf(ATTRIBUTE_PROPTYPE),
  /**
   * updateValue is a function that takes 2 arguments:
   *   - attrTypeName is a string
   *   - value is a number, an array of numbers, or undefined
   */
  updateValue: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

UIDEditModal.defaultProps = {
  attrTypeName: '',
  singleton: false,
  attributes: [],
};

export default UIDEditModal;
